import { flatten, find } from 'lodash';
import type { Industry } from '@/hooks/useIndustryTree';
import type { CountryISO } from '@/constants/countries';
import { parseRules, updateMarketRule, updateIndustryRule } from '../functions';
import { resolveMarkets } from '../components/ModalFilterEditor/util';

const defaultRule = JSON.stringify([
  ['order_by', 'market_cap', 'desc'],
  ['value_score', '>=', 0],
  ['dividends_score', '>=', 0],
  ['future_performance_score', '>=', 0],
  ['health_score', '>=', 0],
  ['past_performance_score', '>=', 0],
  ['grid_visible_flag', '=', true],
  ['market_cap', 'is_not_null'],
  ['primary_flag', '=', true],
  ['is_fund', '=', false],
]);

const resolveIndustries = (
  industrySlug?: string,
  industries: Record<string, Industry>[] = []
) => {
  if (typeof industrySlug === 'undefined') return;
  const [primary, secondary] = industries;
  if (typeof primary === 'undefined' || typeof secondary === 'undefined')
    return;
  let industryId: string | undefined;
  find(primary, (value, key) => {
    if (value.slug === industrySlug) {
      industryId = key;
    }
    return value.slug === industrySlug;
  });
  if (typeof industryId === 'undefined') return;
  const tertiary = primary[industryId].children.map((n) => {
    return secondary[n].children;
  });
  const resolvedIndustries = [
    [industryId],
    primary[industryId].children,
    flatten(tertiary),
  ];
  return resolvedIndustries;
};

type Options = {
  rules?: string;
  marketISO?: CountryISO | 'global';
  industrySlug?: string;
  industries?: Record<string, Industry>[];
};

export const getDefaultRule = (options?: Options) => {
  if (typeof options === 'undefined') return defaultRule;
  const { rules, marketISO, industrySlug, industries = [] } = options;
  let parsedRules = parseRules(rules || defaultRule);
  if (typeof marketISO !== 'undefined' && marketISO !== 'global') {
    const { updatedCountries, updatedExchangeSymbols } = resolveMarkets(
      marketISO,
      0,
      [],
      []
    );
    parsedRules = updateMarketRule(
      updatedCountries,
      updatedExchangeSymbols,
      parsedRules
    );
  }
  const resolvedIndustries = resolveIndustries(industrySlug, industries);
  if (typeof resolvedIndustries !== 'undefined') {
    const [primaryIndustries, secondaryIndustries, tertiaryIndustries] =
      resolvedIndustries;
    parsedRules = updateIndustryRule(
      primaryIndustries,
      secondaryIndustries,
      tertiaryIndustries,
      parsedRules
    );
  }
  return JSON.stringify(parsedRules);
};

export const getScreenerName = (
  gridName: string,
  market: string,
  industry: string
) => {
  if (gridName !== '') return gridName;
  if (market === '' && industry === '') return 'Stock Screener';
  let title = '';
  if (market !== '') {
    title = market;
  }
  if (industry !== '') {
    title = `${title} ${industry}`;
  }
  return `${title} Stocks`;
};

export const checkRulesDirty = (
  rules: string | undefined,
  currentRules: string | undefined
) => {
  if (!rules || !currentRules) {
    return false;
  }
  return rules !== currentRules;
};

type ShouldShowPlanInlineParams = {
  isFetchingCompanies: boolean;
  planType: string;
  isPlanRestriction: boolean;
};

export const shouldShowPlanInline = ({
  isFetchingCompanies,
  planType,
  isPlanRestriction,
}: ShouldShowPlanInlineParams) =>
  !isFetchingCompanies &&
  (planType === 'free' || (planType !== 'unlimited' && isPlanRestriction));

type ShouldShowErrorLimitParams = {
  isRestricted?: boolean | null;
  returnRecentCompaniesOnly: boolean;
  isShowingPlansInline: boolean;
};

export const shouldShowErrorLimit = ({
  isRestricted,
  returnRecentCompaniesOnly,
  isShowingPlansInline,
}: ShouldShowErrorLimitParams) =>
  (isRestricted && returnRecentCompaniesOnly) ||
  (isRestricted && !returnRecentCompaniesOnly && !isShowingPlansInline);
